import { App } from "@/App";
import { OfflineBanner } from "@/components/common/OfflineBanner";
import { Toaster } from "@/components/ui/sonner";
import { setAuthTokenGetter } from "@/lib/api";
import { clerkDarkTheme } from "@/lib/clerk-theme";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "@/lib/i18n";
import "@/styles/globals.css";

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			retry: 1,
		},
	},
});

function AuthTokenSync({ children }: { children: ReactNode }) {
	const { getToken } = useAuth();
	useEffect(() => {
		setAuthTokenGetter(getToken);
	}, [getToken]);
	return <>{children}</>;
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

createRoot(root).render(
	<ClerkProvider publishableKey={CLERK_KEY} appearance={clerkDarkTheme}>
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<AuthTokenSync>
					<OfflineBanner />
					<App />
					<Toaster position="bottom-right" />
				</AuthTokenSync>
			</BrowserRouter>
		</QueryClientProvider>
	</ClerkProvider>,
);
